import {
  acceptReservationRequest,
  createClientSharedLink,
  createEstimate,
  CreateEstimateDto,
  revokeClientSharedLink,
  deleteEstimate,
  duplicateEstimate,
  EstimateOptions,
  EstimateSharedLinkParams,
  getAgencyWebsiteFromEstimateId,
  getEstimateById,
  getEstimateFromSharedLink,
  getEstimateInfo,
  getEstimatesForProject,
  getEstimateSharedLinkToken,
  getIncomingReservationRequest,
  getIncomingReservationRequests,
  refuseReservationRequest,
  ReservationParams,
  ReservationRequestsResponse,
  revokeSharedLink,
  saveEstimate,
  saveEstimateAsClient,
  SaveEstimateDto,
  sendReservationRequest,
  sendReservationRequestAsClient,
  updateEstimateInfo,
  UpdateEstimateInfoDto,
  validateEstimateParagraph,
} from '@/api/estimate.api';
import { queryKeys } from '@/queryKeys';
import {
  keepPreviousData,
  QueryClient,
  useMutation,
  useQuery,
  useQueryClient,
} from '@tanstack/react-query';

// Les listes projets et clients affichent des compteurs et statuts dérivés
// des devis : à invalider dès qu'un devis est créé, modifié ou supprimé.
const invalidateEstimateSummaries = (queryClient: QueryClient) => {
  queryClient.invalidateQueries({ queryKey: queryKeys.projects.all });
  queryClient.invalidateQueries({ queryKey: queryKeys.clients.all });
};

export const useGetEstimates = (projectId: string, options: EstimateOptions) =>
  useQuery({
    queryKey: queryKeys.estimates.byProject(projectId, options),
    queryFn: () => getEstimatesForProject(projectId, options),
    placeholderData: keepPreviousData,
  });

export const useCreateEstimate = (
  projectId: string,
  fromEditableCircuit = false,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateEstimateDto) =>
      createEstimate(data, projectId, fromEditableCircuit),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useDuplicateEstimate = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: string) =>
      duplicateEstimate(estimateId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useDeleteEstimate = (projectId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: string) => deleteEstimate(estimateId, projectId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useGetEstimateById = ({
  estimateId,
  projectId,
}: {
  projectId: string;
  estimateId: string;
}) =>
  useQuery({
    queryKey: queryKeys.estimates.detail(projectId, estimateId),
    queryFn: () => getEstimateById({ projectId, estimateId }),
  });

export const useSaveEstimate = (projectId: string, estimateId: string) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (saveEstimateDto: SaveEstimateDto) =>
      saveEstimate(estimateId, projectId, saveEstimateDto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.detail(projectId, estimateId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useSendReservationRequest = (
  projectId: string,
  estimateId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (saveEstimateDto: SaveEstimateDto) =>
      sendReservationRequest(estimateId, projectId, saveEstimateDto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      invalidateEstimateSummaries(queryClient);
      queryClient.invalidateQueries({
        queryKey: queryKeys.incomingReservationRequests.all,
      });
    },
  });
};

export const useGetIncomingReservationRequests = <
  TData = ReservationRequestsResponse,
>(
  params: ReservationParams,
  select?: (data: ReservationRequestsResponse) => TData,
) =>
  useQuery({
    queryKey: queryKeys.incomingReservationRequests.list(params),
    queryFn: () => getIncomingReservationRequests(params),
    placeholderData: keepPreviousData,
    select,
  });

export const useGetIncomingReservationRequest = (estimateId: string) =>
  useQuery({
    queryKey: queryKeys.incomingReservationRequests.detail(estimateId),
    queryFn: () => getIncomingReservationRequest(estimateId),
  });

export const useAcceptReservationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: string) => acceptReservationRequest(estimateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.incomingReservationRequests.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.all,
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useRefuseReservationRequest = () => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (estimateId: string) => refuseReservationRequest(estimateId),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.incomingReservationRequests.all,
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.all,
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useGetEstimateFromSharedLink = (
  params: EstimateSharedLinkParams,
) =>
  useQuery({
    queryKey: queryKeys.estimates.fromSharedLink(params),
    queryFn: () => getEstimateFromSharedLink(params),
  });

export const useGetEstimateSharedLinkToken = (estimateId: string) =>
  useMutation({
    mutationFn: () => getEstimateSharedLinkToken({ estimateId }),
  });

export const useSaveEstimateAsClient = (
  projectId: string,
  estimateId: string,
  accessToken?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (saveEstimateDto: SaveEstimateDto) =>
      saveEstimateAsClient({
        estimateId,
        projectId,
        saveEstimateDto,
        accessToken,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.fromSharedLink({
          estimateId,
          projectId,
          accessToken,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.detail(projectId, estimateId),
      });
    },
  });
};

export const useSendReservationRequestAsClient = (
  projectId: string,
  estimateId: string,
  accessToken?: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (saveEstimateDto: SaveEstimateDto) =>
      sendReservationRequestAsClient({
        estimateId,
        projectId,
        saveEstimateDto,
        accessToken,
      }),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.fromSharedLink({
          estimateId,
          projectId,
          accessToken,
        }),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.detail(projectId, estimateId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.incomingReservationRequests.all,
      });
    },
  });
};

export const useRevokeSharedLink = (estimateId: string) =>
  useMutation({
    mutationFn: () => revokeSharedLink(estimateId),
  });

// Agency view only: mint the downstream CLIENT link from the AGENCY accessToken.
export const useCreateClientSharedLink = (
  projectId: string,
  estimateId: string,
  accessToken?: string,
) =>
  useMutation({
    mutationFn: () =>
      createClientSharedLink({ projectId, estimateId, accessToken }),
  });

// Agency view only: revoke the downstream CLIENT link via the AGENCY accessToken.
export const useRevokeClientSharedLink = (
  projectId: string,
  estimateId: string,
  accessToken?: string,
) =>
  useMutation({
    mutationFn: () =>
      revokeClientSharedLink({ projectId, estimateId, accessToken }),
  });

export const useUpdateEstimateInfo = (
  estimateId: string,
  projectId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (updateEstimateDto: UpdateEstimateInfoDto) =>
      updateEstimateInfo(estimateId, projectId, updateEstimateDto),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.byProjectBase(projectId),
      });
      queryClient.invalidateQueries({
        queryKey: queryKeys.estimates.info(projectId, estimateId),
      });
      invalidateEstimateSummaries(queryClient);
    },
  });
};

export const useGetEstimateInfo = (estimateId: string, projectId: string) =>
  useQuery({
    queryKey: queryKeys.estimates.info(projectId, estimateId),
    queryFn: () => getEstimateInfo(estimateId, projectId),
  });

export const useGetAgencyWebsiteFromEstimateId = (estimateId: string) =>
  useQuery({
    queryKey: queryKeys.estimates.agencyWebsite(estimateId),
    queryFn: () => getAgencyWebsiteFromEstimateId(estimateId),
  });

export const useValidateEstimateParagraph = (
  projectId: string,
  estimateId: string,
) => {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ field, value }: { field: string; value: string }) =>
      validateEstimateParagraph(projectId, estimateId, field, value),
    onSuccess: async () => {
      await Promise.all([
        queryClient.invalidateQueries({
          queryKey: queryKeys.estimates.detail(projectId, estimateId),
        }),
        queryClient.invalidateQueries({
          queryKey: queryKeys.estimates.fromSharedLinkBase,
        }),
      ]);
    },
  });
};
